import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "@/api";

import { KIND_LABEL } from "@/components/pipeline/LeadTimeline";
import { frDate, today } from "@/components/pipeline/utils";

const DOW = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];
const key = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const localDay = (iso) => { const d = new Date(iso); return key(d.getFullYear(), d.getMonth(), d.getDate()); };
const toneOfDay = (k, remind, list) => (k === remind ? "butter" : list.some((i) => i.kind.startsWith("call")) ? "pine" : list.some((i) => i.kind !== "comment") ? "tomato" : list.length ? "sand" : "");

// Mini-agenda replié : la relance programmée et les échanges, jour par jour
const useStepAt = (leadId, steps, current) => {
  const [hist, setHist] = useState(null);
  useEffect(() => { api.get(`/pipeline/leads/${leadId}/steps`).then(({ data }) => setHist(data)).catch(() => setHist(null)); }, [leadId]);
  return (k) => {
    if (!hist) return null;
    const ch = hist.changes.filter((c) => c.at && localDay(c.at) <= k);
    const guess = !ch.length && hist.changes.length > 0;
    const name = ch.length ? ch[ch.length - 1].step : guess ? steps[0]?.name : current;
    if (!name) return null;
    const i = steps.findIndex((s) => s.name === name);
    return { n: i >= 0 ? i + 1 : ch[ch.length - 1]?.position, name, guess, moved: hist.changes.some((c) => c.at && localDay(c.at) === k) };
  };
};

export const RemindCalendar = ({ leadId, steps = [], currentStep, remindDate, items, saving, onSetRemind }) => {
  const stepAt = useStepAt(leadId, steps, currentStep);
  const now = new Date();
  const start = remindDate ? new Date(`${remindDate}T00:00:00`) : now;
  const [month, setMonth] = useState({ y: start.getFullYear(), m: start.getMonth() });
  const [picked, setPicked] = useState(null);
  const byDay = items.reduce((acc, i) => { if (i.at) (acc[localDay(i.at)] ||= []).push(i); return acc; }, {});
  const first = new Date(month.y, month.m, 1);
  const pad = (first.getDay() + 6) % 7;
  const days = new Date(month.y, month.m + 1, 0).getDate();
  const t0 = today();
  const go = (dir) => setMonth(({ y, m }) => ({ y: m + dir < 0 ? y - 1 : m + dir > 11 ? y + 1 : y, m: (m + dir + 12) % 12 }));
  const label = first.toLocaleDateString("fr-FR", { month: "long", ...(month.y !== now.getFullYear() ? { year: "numeric" } : {}) });
  const dayList = picked ? byDay[picked] || [] : [];
  return (
    <details className="card ldx-cal" open data-testid="lead-schedule">
      <summary data-testid="lead-schedule-toggle"><CalendarDays size={15} /> Agenda de la fiche <small>{remindDate ? `relance ${frDate(remindDate)}` : "aucune relance"} · {items.length} échange{items.length > 1 ? "s" : ""}</small><ChevronDown size={14} className="ldx-cal__chev" /></summary>
      <div className="cal__nav">
        <button type="button" className="ibtn magnetic" onClick={() => go(-1)} aria-label="Mois précédent" data-testid="lead-cal-prev"><ChevronLeft size={17} /></button>
        <button type="button" className="ibtn magnetic" onClick={() => go(1)} aria-label="Mois suivant" data-testid="lead-cal-next"><ChevronRight size={17} /></button>
        <h3 className="cal__month ldp-cap" aria-live="polite" data-testid="lead-cal-month">{label}</h3>
        <button type="button" className="ibtn ibtn--sm" onClick={() => setMonth({ y: now.getFullYear(), m: now.getMonth() })} aria-label="Revenir à ce mois" data-testid="lead-cal-now"><RotateCcw size={14} /></button>
      </div>
      <div className="cal__body">
        <div className="cal__dow" aria-hidden="true">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
        <div className="cal__grid">
          {Array.from({ length: pad }, (_, i) => <span key={`p${i}`} className="day day--pad" aria-hidden="true" />)}
          {Array.from({ length: days }, (_, i) => {
            const k = key(month.y, month.m, i + 1);
            const list = byDay[k] || [];
            const tone = toneOfDay(k, remindDate, list);
            const st = tone ? stepAt(k) : null;
            return (
              <button key={k} type="button" className={`day${tone ? ` day--${tone}` : ""}${k === t0 ? " is-today" : ""}${k < t0 ? " is-past" : ""}${picked === k ? " ldp-picked" : ""}`} onClick={() => setPicked(picked === k ? null : k)} aria-label={`${frDate(k)}${list.length ? `, ${list.length} échange(s)` : ""}${k === remindDate ? ", relance" : ""}`} data-testid={`lead-cal-day-${k}`}>
                <span className="day__n">{i + 1}</span>
                {list.length > 0 && <span className="ldp-day__count">{list.length}</span>}
                {st && <span className={`ldp-day__step${st.moved ? " is-moved" : ""}`} title={`Étape ${st.n || ""} · ${st.name}${st.moved ? " (changée ce jour)" : ""}${st.guess ? " (étape de départ supposée)" : ""}`} data-testid={`lead-cal-step-${k}`}>{st.n ? `${st.n} · ` : ""}{st.name}</span>}
              </button>
            );
          })}
        </div>
      </div>
      <p className="ldp-cal__legend"><i className="day--butter" />Relance <i className="day--pine" />Appel <i className="day--tomato" />E-mail / SMS <i className="day--sand" />Note</p>
      {picked && (
        <div className="ldp-cal__day" data-testid="lead-cal-panel">
          <strong>{frDate(picked)}</strong>
          {stepAt(picked) && <small className="ldp-cal__stepline" data-testid="lead-cal-panel-step">Étape ce jour : {stepAt(picked).n ? `${stepAt(picked).n} · ` : ""}{stepAt(picked).name}{stepAt(picked).moved ? " (changée ce jour)" : ""}</small>}
          {dayList.length ? <ul>{dayList.map((i) => <li key={i.id}>{KIND_LABEL[i.kind] || "Échange"} · {i.title}</li>)}</ul> : <small className="pl-muted">Aucun échange ce jour-là.</small>}
          {picked >= t0 && picked !== remindDate && <button type="button" className="btn btn--sm btn--ink" disabled={saving} onClick={() => { onSetRemind(picked); setPicked(null); }} data-testid="lead-cal-set-remind">Programmer la relance ce jour</button>}
          {picked === remindDate && <small className="pl-muted">Relance programmée ce jour.</small>}
        </div>
      )}
    </details>
  );
};
